import { AnimatePresence } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { Logo } from './components/Logo'
import { Spinner } from './components/Icon'
import { useAuth } from './lib/auth'
import { useGame } from './lib/socket'
import { AdminScreen } from './screens/AdminScreen'
import { AuthScreen } from './screens/AuthScreen'
import { LegalScreen } from './screens/LegalScreen'
import { documentForPath } from './legal/documents'
import { HomeScreen } from './screens/HomeScreen'
import { LobbyScreen } from './screens/LobbyScreen'
import { MatchScreen } from './screens/MatchScreen'
import { ResultOverlay } from './screens/ResultOverlay'
import { WalletScreen } from './screens/WalletScreen'

export function App() {
  const { user, initialising } = useAuth()

  // Deliberately a path check rather than a router: the game is a single
  // socket-driven screen, and pulling in a routing layer to serve one extra
  // page would be more machinery than the problem needs.
  const isAdminPath = window.location.pathname.replace(/\/+$/, '') === '/admin'
  if (isAdminPath) return <AdminScreen />

  // Policies are served before the auth check. The people most likely to read
  // them - a payment reviewer, someone deciding whether to hand over money -
  // do not have an account and should not need one.
  const legal = documentForPath(window.location.pathname)
  if (legal) return <LegalScreen document={legal} />

  if (initialising) return <Splash />
  if (!user) return <AuthScreen />
  return <SignedIn />
}

function SignedIn() {
  const { room, phase, matchEnd, connected, joinRoom } = useGame()
  const handledInvite = useRef(false)
  const [wallet, setWallet] = useState(false)

  // Stripe returns the player to "/?purchase=..." - open the wallet so they
  // land on the balance they just topped up rather than the lobby.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('purchase')) setWallet(true)
  }, [])

  // Honour a `?join=CODE` invite link once the socket is up, then strip it from
  // the URL so a refresh does not try to rejoin a room that has since closed.
  useEffect(() => {
    if (!connected || handledInvite.current) return
    const code = new URLSearchParams(window.location.search).get('join')
    if (!code) return

    handledInvite.current = true
    joinRoom(code)
      .catch(() => {
        // The room is gone or full; the home screen is a fine place to land.
      })
      .finally(() => {
        window.history.replaceState({}, '', window.location.pathname)
      })
  }, [connected, joinRoom])

  const inMatch = room !== null && room.status !== 'lobby' && phase !== 'idle'

  if (wallet && !inMatch && !room) {
    return <WalletScreen onBack={() => setWallet(false)} />
  }

  return (
    <>
      {inMatch ? (
        <MatchScreen />
      ) : room ? (
        <LobbyScreen />
      ) : (
        <HomeScreen onOpenWallet={() => setWallet(true)} />
      )}
      <AnimatePresence>{matchEnd && <ResultOverlay />}</AnimatePresence>
    </>
  )
}

function Splash() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6">
      <Logo size="lg" />
      <Spinner size={22} className="text-fg-dim" />
    </div>
  )
}
